import React, { FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import PhlebotomyCheckbox from "../components/PhlebotomyCheckbox";
import PhlebotomyAddressAutocomplete from "./PhlebotomyAddressAutocomplete";
import type { PhlebotomyStructuredAddress } from "./PhlebotomyAddressAutocomplete";
import {
  AddressFormFields,
  ContactFields,
  IntakeFormActions,
  IntakeSubmitButton,
} from "./PhlebotomySearchPage.styles";
import {
  AddressSummaryCard,
  AddressSummaryLabel,
  AddressSummaryText,
  EditDetailsButton,
  ReviewSummaryCard,
  ReviewSummaryContent,
  ReviewSummaryRow,
  SlotContext,
  StickyBookBar,
} from "./MobilePhlebotomyGetLabsSchedulePage.styles";

export type ScheduleStep = "location" | "times" | "details";
export type Fulfillment = "getlabs" | "care_team";
export type TimeWindow = "morning" | "afternoon" | "anytime";

export interface ScheduleFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
  estimatedDueDate: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  preferredDate: string;
  preferredTimeWindow: TimeWindow | "";
  alternateDate: string;
  alternateTimeWindow: TimeWindow | "";
  flexibleScheduling: boolean;
  hasKit: boolean;
  schedulingNotes: string;
  contactConsent: boolean;
}

export type FieldErrors = Partial<Record<keyof ScheduleFormData, string>>;

const TIME_WINDOW_OPTIONS: readonly TimeWindow[] = [
  "morning",
  "afternoon",
  "anytime",
];

type FieldChangeHandler = (
  field: keyof ScheduleFormData,
) => (event: React.ChangeEvent<HTMLInputElement>) => void;

interface CareTeamScheduleStepProps {
  step: "location" | "times";
  formData: ScheduleFormData;
  fieldErrors: FieldErrors;
  addressSearchInput: string;
  selectedAddress: PhlebotomyStructuredAddress | null;
  useManualAddress: boolean;
  resolvingAddress: boolean;
  alreadyScheduled: boolean;
  fallbackReason: string | null;
  formError: string | null;
  addressSummary: string;
  earliestCareTeamDate: string;
  onLocationSubmit: (event: FormEvent) => void;
  onTimesSubmit: (event: FormEvent) => void;
  onFieldChange: FieldChangeHandler;
  onAddressSearchInputChange: (value: string) => void;
  onAddressSelection: (address: PhlebotomyStructuredAddress | null) => void;
  onAddressBusyChange: (busy: boolean) => void;
  onToggleManualAddress: () => void;
  onKitChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAlreadyScheduledChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onEditAddress: () => void;
  onPreferredDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAlternateDateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  supportActions: React.ReactNode;
}

export const CareTeamScheduleStep: React.FC<CareTeamScheduleStepProps> = ({
  step,
  formData,
  fieldErrors,
  addressSearchInput,
  selectedAddress,
  useManualAddress,
  resolvingAddress,
  alreadyScheduled,
  fallbackReason,
  formError,
  addressSummary,
  earliestCareTeamDate,
  onLocationSubmit,
  onTimesSubmit,
  onFieldChange,
  onAddressSearchInputChange,
  onAddressSelection,
  onAddressBusyChange,
  onToggleManualAddress,
  onKitChange,
  onAlreadyScheduledChange,
  onEditAddress,
  onPreferredDateChange,
  onAlternateDateChange,
  supportActions,
}) => {
  const { t } = useTranslation();

  if (step === "location") {
    return (
      <form onSubmit={onLocationSubmit} noValidate>
        <AddressFormFields>
          <Typography component="h2" variant="subtitle1" fontWeight={700}>
            {t("phlebotomySearch.mobileSchedule.addressSection")}
          </Typography>
          {useManualAddress ? (
            <>
              <TextField
                fullWidth
                required
                autoComplete="address-line1"
                label={t("phlebotomySearch.mobileSchedule.addressLine1")}
                value={formData.addressLine1}
                error={Boolean(fieldErrors.addressLine1)}
                helperText={fieldErrors.addressLine1}
                onChange={onFieldChange("addressLine1")}
              />
              <TextField
                fullWidth
                autoComplete="address-line2"
                label={t("phlebotomySearch.mobileSchedule.addressLine2")}
                value={formData.addressLine2}
                onChange={onFieldChange("addressLine2")}
              />
              <TextField
                fullWidth
                required
                autoComplete="address-level2"
                label={t("phlebotomySearch.mobileSchedule.city")}
                value={formData.city}
                error={Boolean(fieldErrors.city)}
                helperText={fieldErrors.city}
                onChange={onFieldChange("city")}
              />
              <TextField
                fullWidth
                required
                autoComplete="address-level1"
                label={t("phlebotomySearch.mobileSchedule.state")}
                value={formData.state}
                error={Boolean(fieldErrors.state)}
                helperText={fieldErrors.state}
                onChange={onFieldChange("state")}
              />
              <TextField
                fullWidth
                required
                autoComplete="postal-code"
                label={t("phlebotomySearch.mobileSchedule.postalCode")}
                value={formData.postalCode}
                error={Boolean(fieldErrors.postalCode)}
                helperText={fieldErrors.postalCode}
                onChange={onFieldChange("postalCode")}
              />
            </>
          ) : (
            <>
              <PhlebotomyAddressAutocomplete
                label={t("phlebotomySearch.mobileSchedule.addressSearchLabel")}
                inputValue={addressSearchInput}
                value={selectedAddress}
                disabled={false}
                error={Boolean(fieldErrors.addressLine1)}
                helperText={
                  fieldErrors.addressLine1 ||
                  t("phlebotomySearch.mobileSchedule.addressSearchHelp")
                }
                onInputValueChange={onAddressSearchInputChange}
                onChange={onAddressSelection}
                onBusyChange={onAddressBusyChange}
              />
              {selectedAddress && (
                <TextField
                  fullWidth
                  autoComplete="address-line2"
                  label={t("phlebotomySearch.mobileSchedule.addressLine2")}
                  value={formData.addressLine2}
                  onChange={onFieldChange("addressLine2")}
                />
              )}
            </>
          )}
          <Button
            type="button"
            sx={{ alignSelf: "flex-start" }}
            onClick={onToggleManualAddress}
          >
            {t(
              useManualAddress
                ? "phlebotomySearch.mobileSchedule.useAddressSearch"
                : "phlebotomySearch.mobileSchedule.enterAddressManually",
            )}
          </Button>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("phlebotomySearch.mobileSchedule.beforeContinuing")}
            </Typography>
            <FormControlLabel
              sx={{
                alignItems: "flex-start",
                m: 0,
                minWidth: 0,
                "& .MuiCheckbox-root": { p: 0.5, mr: 1 },
                "& .MuiFormControlLabel-label": { pt: 0.5 },
              }}
              control={
                <PhlebotomyCheckbox
                  checked={formData.hasKit}
                  onChange={onKitChange}
                />
              }
              label={t("phlebotomySearch.mobileSchedule.hasKitConfirmation")}
            />
            <FormControlLabel
              sx={{
                alignItems: "flex-start",
                m: 0,
                minWidth: 0,
                "& .MuiCheckbox-root": { p: 0.5, mr: 1 },
                "& .MuiFormControlLabel-label": { pt: 0.5 },
              }}
              control={
                <PhlebotomyCheckbox
                  checked={alreadyScheduled}
                  onChange={onAlreadyScheduledChange}
                />
              }
              label={t(
                "phlebotomySearch.mobileSchedule.alreadyScheduledConfirmation",
              )}
            />
            {alreadyScheduled && supportActions}
          </Box>

          {!alreadyScheduled && (
            <IntakeFormActions>
              <IntakeSubmitButton
                type="submit"
                variant="unity-primary"
                disabled={resolvingAddress}
              >
                {t("phlebotomySearch.mobileSchedule.continueToPreferences")}
              </IntakeSubmitButton>
            </IntakeFormActions>
          )}
        </AddressFormFields>
      </form>
    );
  }

  return (
    <form onSubmit={onTimesSubmit} noValidate>
      <AddressSummaryCard>
        <AddressSummaryLabel>
          {t("phlebotomySearch.mobileSchedule.drawAddress")}
        </AddressSummaryLabel>
        <AddressSummaryText>{addressSummary}</AddressSummaryText>
        <EditDetailsButton type="button" onClick={onEditAddress}>
          {t("phlebotomySearch.mobileSchedule.editDetails")}
        </EditDetailsButton>
      </AddressSummaryCard>

      {fallbackReason && (
        <Alert
          severity="info"
          sx={{ mb: 1.5, py: 0.25, "& .MuiAlert-message": { py: 0.25 } }}
        >
          <Typography fontWeight={700} variant="body2">
            {t("phlebotomySearch.mobileSchedule.fallbackTitle")}
          </Typography>
          <Typography variant="body2">{fallbackReason}</Typography>
        </Alert>
      )}
      {formError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      )}

      <AddressFormFields>
        <Typography
          component="h2"
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 0.5 }}
        >
          {t("phlebotomySearch.mobileRequest.preferencesSection")}
        </Typography>
        <SlotContext>
          {t("phlebotomySearch.mobileRequest.preferredDateHelp")}
        </SlotContext>
        <TextField
          fullWidth
          required
          type="date"
          label={t("phlebotomySearch.mobileRequest.preferredDate")}
          value={formData.preferredDate}
          inputProps={{ min: earliestCareTeamDate }}
          InputLabelProps={{ shrink: true }}
          error={Boolean(fieldErrors.preferredDate)}
          helperText={fieldErrors.preferredDate}
          onChange={onPreferredDateChange}
        />
        <ContactFields>
          <TextField
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label={t("phlebotomySearch.mobileRequest.preferredTimeWindow")}
            value={formData.preferredTimeWindow}
            error={Boolean(fieldErrors.preferredTimeWindow)}
            helperText={fieldErrors.preferredTimeWindow}
            onChange={onFieldChange("preferredTimeWindow")}
          >
            <option value="" disabled>
              {t("phlebotomySearch.mobileRequest.selectTime")}
            </option>
            {TIME_WINDOW_OPTIONS.map((window) => (
              <option key={window} value={window}>
                {t(`phlebotomySearch.mobileRequest.timeWindows.${window}`)}
              </option>
            ))}
          </TextField>
        </ContactFields>
        <TextField
          fullWidth
          type="date"
          label={t("phlebotomySearch.mobileRequest.alternateDate")}
          value={formData.alternateDate}
          inputProps={{ min: earliestCareTeamDate }}
          InputLabelProps={{ shrink: true }}
          error={Boolean(fieldErrors.alternateDate)}
          helperText={fieldErrors.alternateDate}
          onChange={onAlternateDateChange}
        />
        <ContactFields>
          <TextField
            fullWidth
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label={t("phlebotomySearch.mobileRequest.alternateTimeWindow")}
            value={formData.alternateTimeWindow}
            error={Boolean(fieldErrors.alternateTimeWindow)}
            helperText={fieldErrors.alternateTimeWindow}
            onChange={onFieldChange("alternateTimeWindow")}
          >
            <option value="">
              {t("phlebotomySearch.mobileRequest.selectTime")}
            </option>
            {TIME_WINDOW_OPTIONS.map((window) => (
              <option key={window} value={window}>
                {t(`phlebotomySearch.mobileRequest.timeWindows.${window}`)}
              </option>
            ))}
          </TextField>
        </ContactFields>
        <FormControlLabel
          control={
            <PhlebotomyCheckbox
              checked={formData.flexibleScheduling}
              onChange={onFieldChange("flexibleScheduling")}
            />
          }
          label={t("phlebotomySearch.mobileRequest.flexibleScheduling")}
        />
      </AddressFormFields>
      <StickyBookBar>
        <IntakeSubmitButton type="submit" variant="unity-primary">
          {t("phlebotomySearch.mobileSchedule.continueToDetails")}
        </IntakeSubmitButton>
      </StickyBookBar>
    </form>
  );
};

interface ScheduleReviewFormProps {
  formData: ScheduleFormData;
  fieldErrors: FieldErrors;
  fulfillment: Fulfillment;
  appointmentSummary: string;
  addressSummary: string;
  submitting: boolean;
  bookingConflict: boolean;
  formError: string | null;
  submitLabel: string;
  onSubmit: (event: FormEvent) => void;
  onFieldChange: FieldChangeHandler;
  onEditTimes: () => void;
  onEditAddress: () => void;
  supportActions: React.ReactNode;
}

export const ScheduleReviewForm: React.FC<ScheduleReviewFormProps> = ({
  formData,
  fieldErrors,
  fulfillment,
  appointmentSummary,
  addressSummary,
  submitting,
  bookingConflict,
  formError,
  submitLabel,
  onSubmit,
  onFieldChange,
  onEditTimes,
  onEditAddress,
  supportActions,
}) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} noValidate>
      <fieldset
        disabled={submitting}
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      >
        <AddressFormFields>
          <Typography component="h2" variant="subtitle1" fontWeight={700}>
            {t("phlebotomySearch.mobileSchedule.reviewTitle")}
          </Typography>
          <ReviewSummaryCard>
            <ReviewSummaryRow>
              <ReviewSummaryContent>
                <AddressSummaryLabel>
                  {fulfillment === "getlabs"
                    ? t("phlebotomySearch.mobileSchedule.selectedAppointment")
                    : t("phlebotomySearch.mobileSchedule.requestedPreferences")}
                </AddressSummaryLabel>
                <AddressSummaryText>{appointmentSummary}</AddressSummaryText>
              </ReviewSummaryContent>
              <EditDetailsButton type="button" onClick={onEditTimes}>
                {t("phlebotomySearch.mobileSchedule.edit")}
              </EditDetailsButton>
            </ReviewSummaryRow>
            <ReviewSummaryRow>
              <ReviewSummaryContent>
                <AddressSummaryLabel>
                  {t("phlebotomySearch.mobileSchedule.drawAddress")}
                </AddressSummaryLabel>
                <AddressSummaryText>{addressSummary}</AddressSummaryText>
              </ReviewSummaryContent>
              <EditDetailsButton type="button" onClick={onEditAddress}>
                {t("phlebotomySearch.mobileSchedule.edit")}
              </EditDetailsButton>
            </ReviewSummaryRow>
            <ReviewSummaryRow>
              <ReviewSummaryContent>
                <AddressSummaryLabel>
                  {t("phlebotomySearch.mobileSchedule.fulfillmentLabel")}
                </AddressSummaryLabel>
                <AddressSummaryText>
                  {fulfillment === "getlabs"
                    ? t("phlebotomySearch.mobileSchedule.fulfillmentGetLabs")
                    : t("phlebotomySearch.mobileSchedule.fulfillmentCareTeam")}
                </AddressSummaryText>
              </ReviewSummaryContent>
            </ReviewSummaryRow>
          </ReviewSummaryCard>
          <Typography component="h2" variant="subtitle1" fontWeight={700}>
            {t("phlebotomySearch.mobileSchedule.yourDetailsStep")}
          </Typography>
          <ContactFields>
            <TextField
              fullWidth
              required
              autoComplete="given-name"
              label={t("phlebotomySearch.mobileSchedule.firstName")}
              value={formData.firstName}
              error={Boolean(fieldErrors.firstName)}
              helperText={fieldErrors.firstName}
              onChange={onFieldChange("firstName")}
            />
            <TextField
              fullWidth
              required
              autoComplete="family-name"
              label={t("phlebotomySearch.mobileSchedule.lastName")}
              value={formData.lastName}
              error={Boolean(fieldErrors.lastName)}
              helperText={fieldErrors.lastName}
              onChange={onFieldChange("lastName")}
            />
          </ContactFields>
          <TextField
            fullWidth
            required
            autoComplete="tel"
            inputMode="tel"
            label={t("phlebotomySearch.mobileSchedule.phone")}
            value={formData.phoneNumber}
            error={Boolean(fieldErrors.phoneNumber)}
            helperText={fieldErrors.phoneNumber}
            onChange={onFieldChange("phoneNumber")}
          />
          <TextField
            fullWidth
            required
            type="email"
            autoComplete="email"
            label={t("phlebotomySearch.mobileSchedule.email")}
            value={formData.email}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
            onChange={onFieldChange("email")}
          />
          <TextField
            fullWidth
            required
            type="date"
            InputLabelProps={{ shrink: true }}
            label={t("phlebotomySearch.mobileSchedule.dateOfBirth")}
            value={formData.dateOfBirth}
            error={Boolean(fieldErrors.dateOfBirth)}
            helperText={fieldErrors.dateOfBirth}
            onChange={onFieldChange("dateOfBirth")}
          />
          <TextField
            fullWidth
            required
            type="date"
            InputLabelProps={{ shrink: true }}
            label={t("phlebotomySearch.mobileSchedule.estimatedDueDate")}
            value={formData.estimatedDueDate}
            error={Boolean(fieldErrors.estimatedDueDate)}
            helperText={fieldErrors.estimatedDueDate}
            onChange={onFieldChange("estimatedDueDate")}
          />
          <Typography component="h2" variant="subtitle1" fontWeight={700}>
            {t("phlebotomySearch.mobileSchedule.opsSection")}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t("phlebotomySearch.mobileSchedule.schedulingNotes")}
            value={formData.schedulingNotes}
            onChange={onFieldChange("schedulingNotes")}
          />
          <FormControlLabel
            control={
              <PhlebotomyCheckbox
                checked={formData.contactConsent}
                onChange={onFieldChange("contactConsent")}
              />
            }
            label={
              fulfillment === "getlabs"
                ? t("phlebotomySearch.mobileSchedule.contactConsent")
                : t("phlebotomySearch.mobileRequest.contactConsent")
            }
          />
          {fieldErrors.contactConsent && (
            <FormHelperText error>{fieldErrors.contactConsent}</FormHelperText>
          )}
          {(bookingConflict || formError) && (
            <Alert severity="error">
              {bookingConflict
                ? t("phlebotomySearch.mobileSchedule.bookingConflict")
                : formError}
            </Alert>
          )}
          {bookingConflict || (formError && fulfillment === "getlabs") ? (
            supportActions
          ) : (
            <IntakeFormActions>
              <IntakeSubmitButton
                type="submit"
                variant="unity-primary"
                disabled={submitting}
              >
                {submitLabel}
              </IntakeSubmitButton>
            </IntakeFormActions>
          )}
        </AddressFormFields>
      </fieldset>
    </form>
  );
};
